import { useCallback, useEffect, useMemo, useRef } from 'react';

type UseAudioWaveformOptions = {
  drawerState: 'expanded' | 'collapsed' | null;
  isRecordingActive: boolean;
  isPaused: boolean;
};

export const useAudioWaveform = ({
  drawerState,
  isRecordingActive,
  isPaused
}: UseAudioWaveformOptions) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const waveformDataRef = useRef<number[]>(Array(50).fill(0));
  const savedCanvasImageRef = useRef<{
    imageData: ImageData;
    width: number;
    height: number;
  } | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const isWaveformRunningRef = useRef(false);

  const stopAnimation = useCallback(() => {
    isWaveformRunningRef.current = false;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, []);

  const cleanup = useCallback(() => {
    stopAnimation();

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      dataArrayRef.current = null;
    }
  }, [stopAnimation]);

  const setup = useCallback(
    (stream: MediaStream) => {
      cleanup();

      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
      source.connect(analyserRef.current);
    },
    [cleanup]
  );

  const drawCurrentWaveform = useCallback(
    (ctx: CanvasRenderingContext2D, rect: DOMRect) => {
      const barWidth = 2;
      const barGap = 4;
      const barSpacing = barWidth + barGap;
      const maxBars = Math.floor(rect.width / barSpacing);
      const centerY = rect.height / 2;
      const maxBarHeight = rect.height;

      if (isRecordingActive && !isPaused && analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        let sum = 0;
        for (let i = 0; i < dataArrayRef.current.length; i += 1) {
          sum += dataArrayRef.current[i];
        }

        const average = sum / dataArrayRef.current.length;
        const normalizedValue = (average / 255) * 1.5;
        const newValue = Math.max(0.05, Math.min(1.0, normalizedValue));

        waveformDataRef.current = [...waveformDataRef.current.slice(1), newValue];
      }

      for (let i = 0; i < Math.min(maxBars, waveformDataRef.current.length); i += 1) {
        const x = i * barSpacing;
        const normalizedHeight = waveformDataRef.current[i];
        const barHeight = normalizedHeight * maxBarHeight;
        const y = centerY - barHeight / 2;
        const barGradient = ctx.createLinearGradient(0, y, 0, y + barHeight);

        barGradient.addColorStop(0.0048, '#DDF4F9');
        barGradient.addColorStop(0.1476, '#57CAE1');
        barGradient.addColorStop(0.5238, '#DBD7F7');
        barGradient.addColorStop(0.9952, '#FFF3F5');

        ctx.fillStyle = barGradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
    },
    [isPaused, isRecordingActive]
  );

  const drawWaveform = useCallback(() => {
    if (!isWaveformRunningRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    if (savedCanvasImageRef.current) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      canvas.width = savedCanvasImageRef.current.width;
      canvas.height = savedCanvasImageRef.current.height;

      ctx.putImageData(savedCanvasImageRef.current.imageData, 0, 0);
      savedCanvasImageRef.current = null;

      if (isWaveformRunningRef.current && isRecordingActive && !isPaused) {
        animationFrameRef.current = requestAnimationFrame(drawWaveform);
      }
      return;
    }

    ctx.clearRect(0, 0, rect.width, rect.height);
    drawCurrentWaveform(ctx, rect);

    if (isWaveformRunningRef.current && isRecordingActive && !isPaused) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  }, [drawCurrentWaveform, isPaused, isRecordingActive]);

  useEffect(() => {
    if (isRecordingActive && !isPaused && drawerState === 'expanded') {
      isWaveformRunningRef.current = true;
      drawWaveform();
    } else {
      stopAnimation();
    }

    return () => {
      stopAnimation();
    };
  }, [drawWaveform, drawerState, isPaused, isRecordingActive, stopAnimation]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas && drawerState === 'expanded' && isRecordingActive && !isPaused) {
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;

        isWaveformRunningRef.current = true;
        drawWaveform();
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [drawWaveform, drawerState, isPaused, isRecordingActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || drawerState !== 'collapsed') return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      savedCanvasImageRef.current = {
        imageData: ctx.getImageData(0, 0, canvas.width, canvas.height),
        width: canvas.width,
        height: canvas.height
      };
    } catch (error) {
      console.warn('Could not save canvas image:', error);
    }
  }, [drawerState]);

  return useMemo(
    () => ({
      canvasRef,
      setup,
      cleanup
    }),
    [cleanup, setup]
  );
};
